import { AfterViewChecked, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChildren } from '@angular/core';
import { navigation } from '../../../../../data/header-navigation';
import { NavigationLink } from '../../../../shared/interfaces/navigation-link';
import { DirectionService } from '../../../../shared/services/direction.service';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HeaderService } from '../../../../shared/services/header.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'app-header-links',
    templateUrl: './links.component.html',
    styleUrls: ['./links.component.scss']
})
export class LinksComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChildren('submenuElement') submenuElements: QueryList<ElementRef>;
    @ViewChildren('itemElement') itemElements: QueryList<ElementRef>;

    destroy$: Subject<void> = new Subject<void>();

    items: NavigationLink[] = navigation;
    hoveredItem: NavigationLink = null;

    reCalcSubmenuPosition = false;
    /** Hover submenus are drawn in the browser only: the server's HTML reaches the page text early. */
    isBrowser = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private direction: DirectionService,
        private header: HeaderService,
        private zone: NgZone,
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    onItemMouseEnter(item: NavigationLink): void {
        if (this.hoveredItem !== item) {
            this.hoveredItem = item;

            if (item.menu) {
                this.reCalcSubmenuPosition = true;
            }
        }
    }

    onItemMouseLeave(item: NavigationLink): void {
        if (this.hoveredItem === item) {
            this.hoveredItem = null;
        }
    }

    onTouchClick(event, item: NavigationLink): void {
        if (event.cancelable) {
            if (this.hoveredItem && this.hoveredItem === item) {
                return;
            }

            if (item.menu) {
                event.preventDefault();

                this.hoveredItem = item;
                this.reCalcSubmenuPosition = true;
            }
        }
    }

    onOutsideTouchClick(item: NavigationLink): void {
        if (this.hoveredItem === item) {
            this.zone.run(() => this.hoveredItem = null);
        }
    }

    onSubItemClick(): void {
        this.hoveredItem = null;
    }

    /// A parent without a page (Brands): a click, or Enter on the focused button, opens its
    /// menu. Touch is handled by onTouchClick, which cancels the click that would follow.
    onParentClick(item: NavigationLink): void {
        if (this.hoveredItem !== item) {
            this.hoveredItem = item;
            this.reCalcSubmenuPosition = !!item.menu;
        }
    }

    /// ...and it closes when focus leaves the item - tabbing past its last link, say - as
    /// the mouse leaving does. Focus moving within the item (button to link) keeps it open.
    onItemFocusOut(event: FocusEvent, item: NavigationLink): void {
        const to = event.relatedTarget as Node;
        const within = event.currentTarget as HTMLElement;

        if (!to || !within.contains(to)) {
            this.onItemMouseLeave(item);
        }
    }

    ngOnInit(): void {
        merge(
            this.header.navPanelPositionState$,
            this.header.navPanelVisibility$,
        ).pipe(takeUntil(this.destroy$)).subscribe(() => this.hoveredItem = null);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewChecked(): void {
        if (!this.reCalcSubmenuPosition) {
            return;
        }

        if (isPlatformBrowser(this.platformId)){
            
            this.reCalcSubmenuPosition = false;

            const itemElement = this.getCurrentItemElement();
            const submenuElement = this.getCurrentSubmenuElement();
    
            const submenuTop = submenuElement.getBoundingClientRect().top;
            const viewportHeight = window.innerHeight;
            const paddingBottom = 20;
    
            submenuElement.style.maxHeight = `${viewportHeight - submenuTop - paddingBottom}px`;
    
            // calc megamenu position
            if (this.hoveredItem.menu.type !== 'megamenu') {
                return;
            }
    
            const container = submenuElement.offsetParent;
            const containerWidth = container.getBoundingClientRect().width;
            const megamenuWidth = submenuElement.getBoundingClientRect().width;
    
            if (this.direction.isRTL()) {
                const itemPosition = containerWidth - (itemElement.offsetLeft + itemElement.offsetWidth);
                const megamenuPosition = Math.round(Math.min(itemPosition, containerWidth - megamenuWidth));
    
                submenuElement.style.right = megamenuPosition + 'px';
            } else {
                const itemPosition = itemElement.offsetLeft;
                const megamenuPosition = Math.round(Math.min(itemPosition, containerWidth - megamenuWidth));
    
                submenuElement.style.left = megamenuPosition + 'px';
            }
        } 
    }

    getCurrentItemElement(): HTMLDivElement {
        if (!this.hoveredItem) {
            return null;
        }

        const index = this.items.indexOf(this.hoveredItem);
        const elements = this.itemElements.toArray();

        if (index === -1 || !elements[index]) {
            return null;
        }

        return elements[index].nativeElement as HTMLDivElement;
    }

    getCurrentSubmenuElement(): HTMLDivElement {
        if (!this.hoveredItem) {
            return null;
        }

        const index = this.items.filter(x => x.menu).indexOf(this.hoveredItem);
        const elements = this.submenuElements.toArray();

        if (index === -1 || !elements[index]) {
            return null;
        }

        return elements[index].nativeElement as HTMLDivElement;
    }
}
